/* "⇄ Switched tasks…" (owner request 2026-10-15): the running timer is ended at the moment the person really switched and
   the new work starts then, with no overlap: the old entry ends 1 second before the switch moment S (Magic Time Sync's rule
   "start 1 second after the previous ended"), the new timer starts at S. Pure arithmetic only (tested in tests/app/switch.test.ts).
   Every time here is a real instant (ms); the zone is only for reading a typed clock time and for the words of a message. */
import {wallOf, wallToMsList, isGapWall, gapMessage, addDays, SYNC_GAP_MS, WS_TZ} from './time.ts';

/** the "I switched" choices, in minutes before now (0 = Just now) */
export const SWITCH_AGO = [0, 5, 10, 15, 20, 30];
const sec = (ms: number) => Math.floor(ms / 1000) * 1000;
/** "9:05:12 AM" for an instant in a zone */
export function clockSec(ms: number, tz = WS_TZ): string {
  const w = wallOf(ms, tz), h = +w.slice(11, 13);
  return (h % 12 || 12) + w.slice(13, 19) + (h < 12 ? ' AM' : ' PM');
}
/** the switch moment N minutes before now, to the whole second (never in the future) */
export const agoMoment = (nowMs: number, min: number) => sec(nowMs) - min * 60000;

export interface SwitchPlan {
  ok: true;
  /** S: the new timer starts here (a whole second) */ newStartMs: number;
  /** the old entry: its stored start (the timer's start to the whole second, as stored), end = S − 1 s, whole seconds between */
  oldStartMs: number; oldEndMs: number; durationSeconds: number;
  /** what stopping the old timer now would have given (shown as "instead of") */ wasSeconds: number;
  /** how long the new timer will already have run, now */ runningSeconds: number;
}
export type SwitchResult = SwitchPlan | {ok: false; error: string};

/** the switch at switchMs for a timer started at timerStartMs. Refused: a moment in the future, or one that leaves the old
    entry less than 1 second (you cannot switch before the timer started). The duration is counted from instants, never
    from clock strings: (S − 1 s) − the stored start (the timer's start without its fraction of a second, which is what the
    entry's wall clock keeps), so the saved entry's end is exactly S − 1 s. */
export function planSwitch(timerStartMs: number, switchMs: number, nowMs: number, tz = WS_TZ): SwitchResult {
  if (!isFinite(timerStartMs) || !isFinite(switchMs)) return {ok: false, error: 'Pick when you switched.'};
  const s = sec(switchMs), start = sec(timerStartMs), end = s - SYNC_GAP_MS, dur = Math.round((end - start) / 1000);
  if (s > nowMs) return {ok: false, error: 'That is in the future. Pick a time before now.'};
  if (dur < 1) return {ok: false, error: `You cannot switch before the timer started (it started ${clockSec(timerStartMs, tz)}). Pick a later time.`};
  return {ok: true, newStartMs: s, oldStartMs: start, oldEndMs: end, durationSeconds: dur,
          wasSeconds: Math.max(1, Math.round((nowMs - timerStartMs) / 1000)), runningSeconds: Math.max(0, Math.round((nowMs - s) / 1000))};
}

/** a typed clock time ("14:05" or "14:05:30", the person's zone) as the switch moment: today's, or, when that is still to
    come, yesterday's (only if that is after the timer started; past midnight). A time the clocks skip (spring) is refused with
    gapMessage, never moved. In a repeated hour (November) the later of the two that have already happened. */
export function typedMoment(time: string, nowMs: number, timerStartMs: number, tz = WS_TZ): {ms: number} | {error: string} {
  const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time || '');
  if (!m) return {error: 'Type a time like 2:05 PM.'};
  const t = m[1] + ':' + m[2] + ':' + (m[3] || '00'), today = wallOf(nowMs, tz).slice(0, 10);
  const at = (day: string): {ms: number} | {error: string} | null => {
    const w = day + 'T' + t;
    if (isGapWall(w, tz)) return {error: gapMessage(w, tz)};
    const past = wallToMsList(w, tz).filter(ms => ms <= nowMs);
    return past.length ? {ms: past[past.length - 1]} : null;
  };
  const a = at(today);
  if (a) return a;
  const b = at(addDays(today, -1));
  if (b && 'error' in b) return b;
  if (b && planSwitch(timerStartMs, b.ms, nowMs, tz).ok) return b;
  return {error: `${clockSec(wallToMsList(today + 'T' + t, tz)[0] ?? nowMs, tz)} today is still to come${b ? `, and yesterday's was before the timer started (${clockSec(timerStartMs, tz)})` : ''}. Pick a time before now.`};
}
